import { computed, ref, watch, watchEffect } from 'vue';
import type { AuthUser, UserRow } from '../types/auth';
import { supabaseClient } from '@/services/supabase';
import { isNonNullable, isNullable } from '@/utils/nullability-helpers';
import { fetchUser } from '@/services/auth-service';
import { asyncComputed, createGlobalState, useLocalStorage } from '@vueuse/core';
import type { Organization } from '@/types/organizations';
import type { OrganizationMetadata } from '@shared-types/organizations';
import { defaultOrganizationId } from '@/utils/env-helpers';
import { getPublicUser } from '@/services/auth-service';
import { setUser as setSentryUser } from '@sentry/vue';

export const useAuthStore = createGlobalState(() => {
    const user = ref<AuthUser | undefined>(undefined);
    const isFetchingUser = ref(false);
    // user.value === undefined is ambiguous between "haven't checked yet" and "checked, no
    // session" — without this, fetchUserIfNecessary() re-fetches (and re-toggles isFetchingUser,
    // which App.vue uses to gate <router-view>) every time it's called for a session-less user.
    const hasCheckedAuth = ref(false);
    let isFetchingUserPromise: Promise<AuthUser | undefined> | undefined;

    const isSignedIn = computed(() => isNonNullable(user.value));

    async function fetchUserIfNecessary(): Promise<AuthUser | undefined> {
        if (isNullable(user.value) && !hasCheckedAuth.value) {
            if (isFetchingUser.value) {
                return isFetchingUserPromise;
            }
            try {
                isFetchingUser.value = true;
                isFetchingUserPromise = fetchUser();
                user.value = await isFetchingUserPromise;
            } finally {
                isFetchingUserPromise = undefined;
                isFetchingUser.value = false;
                hasCheckedAuth.value = true;
            }
        }
        return user.value;
    }

    async function refreshUser(): Promise<AuthUser | undefined> {
        user.value = await fetchUser();
        return user.value;
    }

    supabaseClient.auth.onAuthStateChange((_, session) => {
        const nextUser = session?.user as AuthUser | undefined;
        const currentId = user.value?.id;
        const nextId = nextUser?.id;
        // Avoid reassigning the same user on token refresh events.
        if (currentId === nextId) {
            return;
        }
        user.value = nextUser;
    });

    // Redirect to login when an active session expires (e.g. refresh token exhausted).
    // Dynamic import avoids a circular dependency with the router module.
    watch(user, async (newUser, oldUser) => {
        if (isNonNullable(oldUser) && isNullable(newUser)) {
            const { default: router } = await import('@/router');
            const { redirectAfterSignOut } = await import('@/services/auth-service');
            const current = router.currentRoute.value;
            // Pages marked noAuth (e.g. JoinOrganizationView) render their own signed-out UI
            // in place and don't want to be forced off their current route on sign-out.
            if (current.meta.noAuth) {
                return;
            }
            redirectAfterSignOut(currentOrganization.value?.metadata?.logoutRedirectUrl, router, {
                name: 'login',
                query: { redirect: encodeURIComponent(current.fullPath) },
            });
        }
    });

    const possibleOrganizationIds = computed(() => {
        if (isNullable(user.value?.app_metadata.organizations)) {
            return [];
        }
        return Object.keys(user.value.app_metadata.organizations);
    });

    const isPGA = computed(() => {
        return currentOrganization.value?.metadata?.isPGA ?? false;
    });

    const userSelectedOrganizationId = useLocalStorage<string | undefined>('userSelectedOrganizationId', undefined);

    const selectedOrganizationId = computed<string | undefined>(() => {
        if (
            isNonNullable(userSelectedOrganizationId.value) &&
            possibleOrganizationIds.value.includes(userSelectedOrganizationId.value)
        ) {
            return userSelectedOrganizationId.value;
        }
        return possibleOrganizationIds.value[0];
    });

    const selectedOrganizationRole = computed<string | undefined>(() => {
        if (isNullable(selectedOrganizationId.value)) {
            return undefined;
        }
        return user.value?.app_metadata.organizations[selectedOrganizationId.value][0];
    });

    const loadedOrganizations = ref<Organization[]>([]);

    // refresh the page when the group is changed to load relevant group related data
    watch(userSelectedOrganizationId, (newOrganizationId, oldOrganizationId) => {
        if (newOrganizationId !== oldOrganizationId) {
            window.location.reload();
        }
    });

    watch(
        possibleOrganizationIds,
        async () => {
            if (possibleOrganizationIds.value.length === 0) {
                loadedOrganizations.value = [];
                return;
            }

            const { data } = await supabaseClient
                .from('organizations')
                .select('*')
                .in('id', possibleOrganizationIds.value);
            if (isNonNullable(data)) {
                loadedOrganizations.value = data.map((organization) => ({
                    id: organization.id,
                    metadata: organization.metadata as OrganizationMetadata,
                    status: organization.status as 'active' | 'suspended',
                    suspendedAt: organization.suspended_at,
                    suspendedBy: organization.suspended_by,
                    suspensionReason: organization.suspension_reason,
                    autoDeleteAt: organization.auto_delete_at,
                }));
            }
        },
        { immediate: true },
    );

    const organizations = computed(() =>
        possibleOrganizationIds.value
            .map((id) => loadedOrganizations.value.find((g) => g.id === id))
            .filter(isNonNullable),
    );

    const currentOrganization = computed(() =>
        loadedOrganizations.value.find((g) => g.id === selectedOrganizationId.value),
    );

    // Default organization is the one that has super admin privileges
    // We should probably create a different app (codebase) for super admins to manage application data
    // or think of something else
    // Handling the access just via javascript is not so secure
    const isDefaultOrganization = computed(() => selectedOrganizationId.value === defaultOrganizationId);

    function onOrganizationSwitch(callback: () => void) {
        watch(selectedOrganizationId, (newOrganizationId, oldOrganizationId) => {
            if (isNonNullable(oldOrganizationId) && newOrganizationId !== oldOrganizationId) {
                callback();
            }
        });
    }

    const isLoggedIn = computed(() => isNonNullable(user.value));
    const isAdmin = computed(() => selectedOrganizationRole.value === 'admin');
    const isDepartmentAdmin = computed(() => selectedOrganizationRole.value === 'department_admin');
    const isManager = computed(() => selectedOrganizationRole.value === 'manager');
    const isParticipant = computed(() => selectedOrganizationRole.value === 'participant');
    const isInstructor = computed(
        () => selectedOrganizationRole.value === 'instructor' || selectedOrganizationRole.value === 'admin',
    );

    const isSuperAdmin = computed(() => {
        if (
            isNullable(user.value) ||
            isNullable(user.value.app_metadata.organizations) ||
            isNullable(user.value.app_metadata.organizations[defaultOrganizationId])
        ) {
            return false;
        }
        return user.value?.app_metadata.organizations[defaultOrganizationId][0] === 'admin';
    });

    const currentAccessRole = ref<'participant' | 'admin'>('participant');

    // Function to refresh current organization data
    const refreshCurrentOrganization = async (): Promise<void> => {
        if (isNullable(selectedOrganizationId.value)) {
            return;
        }

        const { data } = await supabaseClient
            .from('organizations')
            .select('*')
            .eq('id', selectedOrganizationId.value)
            .single();

        if (isNonNullable(data)) {
            const updatedOrganization = {
                id: data.id,
                metadata: data.metadata as OrganizationMetadata,
                status: data.status as 'active' | 'suspended',
                suspendedAt: data.suspended_at,
                suspendedBy: data.suspended_by,
                suspensionReason: data.suspension_reason,
                autoDeleteAt: data.auto_delete_at,
            };

            // Update the organization in the loaded organizations array
            const index = loadedOrganizations.value.findIndex((org) => org.id === selectedOrganizationId.value);
            if (index !== -1) {
                loadedOrganizations.value[index] = updatedOrganization;
            } else {
                loadedOrganizations.value.push(updatedOrganization);
            }
        }
    };

    // Managed departments for department_admins
    const managedDepartments = ref<string[]>([]);
    watchEffect(async () => {
        if (user.value && selectedOrganizationId.value && isDepartmentAdmin.value) {
            const { data, error } = await supabaseClient
                .from('department_roles_users_relations')
                .select('department_id')
                .eq('user_id', user.value.id)
                .eq('organization_id', selectedOrganizationId.value);
            if (!error && data) {
                managedDepartments.value = Array.from(new Set(data.map((row: any) => row.department_id)));
            } else {
                managedDepartments.value = [];
            }
        } else {
            managedDepartments.value = [];
        }
    });

    // undefined = fetch not yet complete (pass-through), null = no row found
    const currentUserLicense = ref<
        { status: string; expires_at: string | null; starts_at: string | null } | null | undefined
    >(undefined);
    const orgHasPool = ref<boolean | undefined>(undefined);

    watch(
        [user, selectedOrganizationId],
        async ([newUser, newOrgId]) => {
            if (!newUser || !newOrgId) {
                currentUserLicense.value = undefined;
                orgHasPool.value = undefined;
                return;
            }
            const [licenseResult, poolResult] = await Promise.all([
                supabaseClient
                    .from('user_licenses')
                    .select('status, expires_at, starts_at')
                    .eq('organization_id', newOrgId)
                    .maybeSingle(),
                supabaseClient.from('org_license_pools').select('id').eq('organization_id', newOrgId).maybeSingle(),
            ]);
            if (licenseResult.error) {
                console.warn('[auth] license fetch failed:', licenseResult.error.message);
                currentUserLicense.value = undefined; // leave as pass-through on error
            } else {
                currentUserLicense.value = licenseResult.data ?? null;
            }
            if (poolResult.error) {
                console.warn('[auth] pool fetch failed:', poolResult.error.message);
                orgHasPool.value = undefined; // leave as pass-through on error
            } else {
                orgHasPool.value = poolResult.data !== null;
            }
        },
        { immediate: true },
    );

    // super admins bypass license checks entirely; for others: loading/error → pass-through;
    // no pool → unrestricted; pool exists → require active, started, non-expired license
    const isLicensed = computed(() => {
        if (isSuperAdmin.value) return true;
        if (currentUserLicense.value === undefined || orgHasPool.value === undefined || orgHasPool.value === false)
            return true;
        if (!currentUserLicense.value || currentUserLicense.value.status !== 'active') return false;
        const now = new Date();
        if (currentUserLicense.value.starts_at != null && new Date(currentUserLicense.value.starts_at) > now)
            return false;
        if (currentUserLicense.value.expires_at != null && new Date(currentUserLicense.value.expires_at) < now)
            return false;
        return true;
    });

    const publicUser = asyncComputed<UserRow | undefined>(() => {
        if (isNullable(user.value?.id)) {
            return Promise.resolve(undefined);
        }
        return getPublicUser(user.value.id);
    });

    watch(
        user,
        (newUser) => {
            if (isNonNullable(newUser)) {
                setSentryUser({
                    id: newUser.id,
                });
            } else {
                setSentryUser(null);
            }
        },
        { immediate: true },
    );

    return {
        user,
        isSignedIn,
        organizationId: selectedOrganizationId,
        loadedOrganizations,
        organizations,
        currentOrganization,
        isDefaultOrganization,
        userSelectedOrganizationId,
        possibleOrganizationIds,
        onOrganizationSwitch,
        role: selectedOrganizationRole,
        isInstructor,
        isAdmin,
        isDepartmentAdmin,
        isManager,
        isParticipant,
        isLoggedIn,
        fetchUserIfNecessary,
        refreshUser,
        isFetchingUser,
        isSuperAdmin,
        refreshCurrentOrganization,
        managedDepartments,
        publicUser,
        currentAccessRole,
        isLicensed,
        orgHasPool,
        isPGA,
    };
});
